import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { NavLink } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useBranding } from '../contexts/BrandingContext';
import { getSidebarSections } from './sidebarNavigation';

const MobileSidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const { user, logout, isCoach, isParent } = useAuth();
  const colors = useTheme();
  const { brandTitle, brandLogo } = useBranding();
  const sections = getSidebarSections({ role: user?.role, isCoach, isParent });

  const handleLogout = () => {
    logout();
    setSidebarOpen(false);
  };

  return (
    <Transition.Root show={sidebarOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/80" />
        </Transition.Child>

        <div className="fixed inset-0 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button
                    type="button"
                    className="-m-2.5 p-2.5"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>

              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-800 pb-4">
                <div
                  className="flex h-16 shrink-0 items-center px-6"
                  style={{ backgroundColor: colors.primary }}
                >
                  {brandLogo && (
                    <img
                      src={brandLogo}
                      alt={`${brandTitle} logo`}
                      className="h-8 w-auto max-w-[88px] object-contain mr-2"
                    />
                  )}
                  <h1 className="text-xl font-bold text-white truncate" title={brandTitle}>{brandTitle}</h1>
                </div>
                <nav className="flex flex-1 flex-col px-6">
                  <ul className="flex flex-1 flex-col gap-y-7">
                    {sections.map((section) => (
                      <li key={section.key}>
                        <ul className="-mx-2 space-y-1">
                          {section.items.map((item) => (
                            <li key={item.name}>
                              <NavLink
                                to={item.href}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) =>
                                  `group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold ${
                                    isActive
                                      ? 'text-white'
                                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                                  }`
                                }
                                style={({ isActive }) =>
                                  isActive ? { backgroundColor: colors.primary } : {}
                                }
                              >
                                <item.icon
                                  className="h-6 w-6 shrink-0"
                                  aria-hidden="true"
                                />
                                {item.name}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                    <li className="mt-auto">
                      <div className="border-t border-gray-700 pt-4">
                        <div className="flex items-center gap-x-4 px-2 py-3 text-sm font-semibold leading-6 text-white">
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: colors.primary }}
                          >
                            <span className="text-white font-medium text-sm">
                              {user?.name?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="truncate">{user?.name || 'User'}</p>
                            <p className="text-xs text-gray-300 capitalize">{user?.role || 'Member'}</p>
                          </div>
                        </div>
                        
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-x-3 px-2 py-4 text-sm font-semibold leading-6 text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
                        >
                          <svg className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign out
                        </button>
                      </div>
                    </li>
                  </ul>
                </nav>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default MobileSidebar;
